import { useEffect, useState } from 'react'

import './_accountSettings.scss'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import FixedBottomButton from 'components/UI/FixedBottomButton/FixedBottomButton'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import {
    putEmail,
    sendSecurityVerificationCode,
    verifySecurityVerificationCode
} from 'services/endpoints/settings/account/profileSettings'
import {
    useSetUserProfile,
    useUserProfile
} from 'store'

import { ConfirmEmailChangePassword } from './components/ConfirmEmailChangePassword'

interface ConfirmSecurityCodeProps {
    loading: boolean
    onConfirm: (code: string) => void
    onResend: () => void
    resendLoading: boolean
}

const ConfirmSecurityCode = ({
    loading,
    onConfirm,
    onResend,
    resendLoading
}: ConfirmSecurityCodeProps) => {
    const [code, setCode] = useState('')
    const { t } = useTranslation()

    return (
        <div className='security-code-modal'>
            <InputCard
                type='text'
                label={t('verificationCode')}
                value={code}
                changeFn={(value: string) => {
                    setCode(
                        value
                            .replace(/\D/g, '')
                            .slice(0, 6)
                    )
                }}
            />

            <div className='security-code-modal__sent'>
                {t('A verification code was sent to your email.')}
            </div>

            <div className='security-code-modal__actions'>
                <Button
                    text={t('resendCode')}
                    color='black'
                    font='mont-14-normal'
                    height='4'
                    width='14'
                    disabled={resendLoading}
                    clickFn={onResend}
                />

                <Button
                    text={t('verify')}
                    color='blue'
                    font='mont-14-normal'
                    height='4'
                    width='14'
                    disabled={code.length !== 6 || loading}
                    clickFn={() => {
                        if (code.length !== 6) return

                        onConfirm(code)
                    }}
                />
            </div>
        </div>
    )
}

export const ChangeEmail = () => {
    const [emailState, setEmailState] = useState({
        newEmail: '',
        recoveryEmail: ''
    })

    const [currentPage, setCurrentPage] = useState<number>(1)

    /*
     * We keep the password temporarily so it can be submitted
     * only after the email verification code has succeeded.
     */
const [confirmedPassword, setConfirmedPassword] = useState('')
const [verificationId, setVerificationId] = useState<string | null>(null)

    const navigate = useNavigate()
    const userData = useUserProfile()
    const modal = useModalContext()
    const { t } = useTranslation()

    useEffect(() => {
        if (!userData) return

        setEmailState({
            newEmail: userData.email ?? '',
            recoveryEmail:
                userData.recovery_email ?? ''
        })
    }, [userData])

    const validateInput = (text: string): boolean => {
        return (
            /\S+@\S+\.\S+/.test(text) &&
            text.length > 0
        )
    }

    const getApiErrors = (error: any) => {
        const response = error?.response?.data

        return (
            response?.errors ??
            response?.error?.errors ??
            (
                typeof response?.message === 'object'
                    ? response.message
                    : null
            )
        )
    }

const updateEmail = useMutation(
    async (password: string) => {
        if (!verificationId) {
            throw new Error(
                'Missing security verification ID.'
            )
        }

        return await putEmail({
            email: emailState.newEmail.trim(),
            password,
            verification_id: verificationId,
            recovery_email:
                emailState.recoveryEmail.trim() ||
                undefined
        })
    },
        {
onSuccess: () => {
    useSetUserProfile({
        email: emailState.newEmail.trim().toLowerCase()
    })

    setVerificationId(null)
    setConfirmedPassword('')

    modal.clearModal()

    navigate('/settings/account')
},
            onError: (error: any) => {
                const errors = getApiErrors(error)

                if (errors?.password?.length) {
                    addToast(
                        'error',
                        String(errors.password[0])
                    )

                    return
                }

                if (
                    errors?.security_verification?.length
                ) {
                    addToast(
                        'error',
                        String(
                            errors.security_verification[0]
                        )
                    )

                    return
                }

                if (errors?.email?.length) {
                    addToast(
                        'error',
                        String(errors.email[0])
                    )

                    return
                }

                if (
                    errors?.recovery_email?.length
                ) {
                    addToast(
                        'error',
                        String(
                            errors.recovery_email[0]
                        )
                    )

                    return
                }

                addToast(
                    'error',
                    'Something went wrong'
                )
            }
        }
    )

    const openCodeConfirmation = () => {
        modal.clearModal()

        modal.addModal(
            t('verifyEmail'),
            <ConfirmSecurityCode
                loading={
                    verifyCodeMutation.isLoading
                }
                resendLoading={
                    sendCodeMutation.isLoading
                }
                onConfirm={(code: string) => {
                    verifyCodeMutation.mutate(code)
                }}
                onResend={() => {
                    sendCodeMutation.mutate()
                }}
            />
        )
    }

const sendCodeMutation = useMutation(
    async () => {
        return await sendSecurityVerificationCode(
            'change_email',
            {
                email: emailState.newEmail.trim(),
                recovery_email:
                    emailState.recoveryEmail.trim() || null
            }
        )
    },
    {
        onSuccess: response => {
            const id = response?.data?.verification_id

            if (!id) {
                addToast(
                    'error',
                    'Unable to create security verification.'
                )

                return
            }

            setVerificationId(id)

            addToast(
                'success',
                'Verification code sent to your email.'
            )

            openCodeConfirmation()
        },

        onError: (error: any) => {
            const errors = getApiErrors(error)

            if (errors?.email?.length) {
                addToast(
                    'error',
                    String(errors.email[0])
                )

                return
            }

            addToast(
                'error',
                'Unable to send verification code.'
            )
        }
    }
)

const verifyCodeMutation = useMutation(
    async (code: string) => {
        if (!verificationId) {
            throw new Error(
                'Missing security verification ID.'
            )
        }

        return await verifySecurityVerificationCode(
            verificationId,
            code
        )
    },
        {
            onSuccess: () => {
                /*
                 * The verification is now marked valid on the
                 * backend for the short security window.
                 *
                 * Submit the real email change with the current
                 * password as the second security requirement.
                 */
                updateEmail.mutate(
                    confirmedPassword
                )
            },

            onError: (error: any) => {
                const errors = getApiErrors(error)

                if (errors?.code?.length) {
                    addToast(
                        'error',
                        String(errors.code[0])
                    )

                    return
                }

                addToast(
                    'error',
                    'Invalid verification code.'
                )
            }
        }
    )

    const openPasswordConfirmation = () => {
        if (
            !validateInput(
                emailState.newEmail
            )
        ) {
            return
        }

        modal.addModal(
            t('confirmEmailChange'),
            <ConfirmEmailChangePassword
                loading={
                    sendCodeMutation.isLoading
                }
                onConfirm={(
                    password: string
                ) => {
                    /*
                     * Keep the password only for this change flow.
                     * Once the code succeeds, it is submitted to
                     * change-email and then cleared.
                     */
                    setConfirmedPassword(password)

                    sendCodeMutation.mutate()
                }}
            />
        )
    }

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <>
                        <InputCard
                            hasIcon={true}
                            icon={
                                Icons.mailoutline
                            }
                            type='text'
                            label={t('email')}
                            value={
                                emailState.newEmail
                            }
                            changeFn={(
                                val: string
                            ) => {
                                setEmailState(
                                    prev => ({
                                        ...prev,
                                        newEmail: val
                                    })
                                )
                            }}
                            validate='email'
                        />

                        <InputCard
                            hasIcon={true}
                            icon={
                                Icons.mailoutline
                            }
                            type='text'
                            label={t(
                                'recoverEmail'
                            )}
                            value={
                                emailState.recoveryEmail
                            }
                            changeFn={(
                                val: string
                            ) => {
                                setEmailState(
                                    prev => ({
                                        ...prev,
                                        recoveryEmail:
                                            val
                                    })
                                )
                            }}
                            validate='email'
                        />
                    </>
                )

            case 2:
                return (
                    <SuccessfulChange
                        img={Icons.email_big}
                        text='You have successfully changed your email.'
                    />
                )

            default:
                return null
        }
    }

    const renderBottomButton = () => {
        switch (currentPage) {
            case 1:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('update')}
                        disabled={
                            !validateInput(
                                emailState.newEmail
                            ) ||
                            sendCodeMutation.isLoading ||
                            verifyCodeMutation.isLoading ||
                            updateEmail.isLoading
                        }
                        clickFn={
                            openPasswordConfirmation
                        }
                    />
                )

            case 2:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('close')}
                        clickFn={() => {
                            navigate(
                                '/settings/account'
                            )
                        }}
                    />
                )

            default:
                return null
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('account')}
                    title={t(
                        'settings:changeEmail'
                    )}
                />
            }
        >
            <>
                {renderCurrentPage()}
                {renderBottomButton()}
            </>
        </WithHeaderSection>
    )
}